import { Children, ReactNode, useCallback, useEffect, useId } from "react";
import { useIntl } from "react-intl";
import Tooltip from "./Tooltip.tsx";
import { clsx } from "clsx";
import ToastContainer from "../../routes/root/core-ui/toast/ToastContainer.tsx";
import { createPortal } from "react-dom";
import useDialog from "./useDialog.tsx";
import styles from "./DialogFrame.module.scss";
import useResizeObserver from "./useResizeObserver.tsx";
import { mergeRefs } from "react-merge-refs";

interface DialogFrameProps {
  children: ReactNode;
  title?: ReactNode;
  onRequestClose: () => void;
  size?: "small";
  darkHeader?: boolean;
  dialogClassName?: string;
  canClose?: boolean;
  fullScreen?: boolean;
  customWidth?: string;
}

export const toastContainerId = "dialog-toast";

export function DialogBody({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx("vst-dialog-component-body", className)}>
      {children}
    </div>
  );
}

export function DialogFooter({
  children,
  className,
  center,
  reverseButtons,
}: {
  children: ReactNode;
  className?: string;
  center?: boolean;
  reverseButtons?: boolean;
}) {
  return (
    <div
      className={clsx(
        "vst-dialog-component-footer",
        className,
        center && styles.centeredFooter
      )}
    >
      {reverseButtons ? Children.toArray(children).reverse() : children}
    </div>
  );
}

export const DialogFrame = ({
  children,
  title,
  onRequestClose,
  size,
  darkHeader,
  dialogClassName,
  canClose = true,
  fullScreen = false,
  customWidth = "",
}: DialogFrameProps) => {
  const intl = useIntl();
  const { dialogRef, scrollRef } = useDialog(onRequestClose);
  const titleId = useId();
  const dialogId = useId();
  const { ref: resizeRef } = useResizeObserver();

  const isScrollable = scrollRef.current
    ? scrollRef.current.scrollHeight > scrollRef.current.clientHeight
    : false;
  const customStyles = customWidth ? { width: customWidth } : {};

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (scrollRef.current && event.key === "Tab" && isScrollable) {
        const footerElement = document.querySelector(
          ".vst-dialog-component-footer"
        );
        const footerHeight = footerElement ? footerElement.clientHeight : 0;
        if (footerHeight) {
          scrollRef.current.style.scrollPaddingBottom = `${footerHeight}px`;
        }
      }
    },
    [isScrollable, scrollRef]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (scrollRef.current && !isScrollable) {
      scrollRef.current.style.scrollPaddingBottom = "";
    }
  }, [isScrollable, scrollRef]);

  return createPortal(
    <div
      id={dialogId}
      ref={dialogRef}
      className={clsx(
        "vst-dialog",
        size === "small" && "vst-dialog-size-small",
        darkHeader && "vst-dialog-variant-banner",
        isScrollable && "vst-dialog-option-sticky-footer",
        fullScreen && "vst-dialog-full-screen"
      )}
      aria-labelledby={titleId}
      aria-hidden="true"
      // These are normally provided by a11y-dialog but there can sometimes
      // be a timing issue leading to auto-closing of dialogs opened from
      // the sidebar on small screens
      role="dialog"
      aria-modal="true"
    >
      <div
        className="vst-dialog-component-backdrop"
        data-a11y-dialog-hide={canClose || null}
      />
      <div
        style={customStyles}
        className={clsx("vst-dialog-component-main", dialogClassName)}
        ref={mergeRefs([scrollRef, resizeRef])}
      >
        <button
          className="vst-dialog-component-close"
          disabled={!canClose}
          aria-label={intl.formatMessage({ defaultMessage: "Close" })}
          data-tooltip-content={intl.formatMessage({
            defaultMessage: "Close",
          })}
          data-tooltip-place="top"
          data-a11y-dialog-hide="true"
        />
        {title && (
          <h1 className="vst-dialog-component-header" id={titleId}>
            {title}
          </h1>
        )}
        {children}
      </div>
      <ToastContainer targetId={toastContainerId} />
      <Tooltip anchorSelect="button,a" />
    </div>,
    document.body
  );
};
